import {ShippingModals} from "./ShippingModals";
import {ShippingActions} from "./ShippingActions";
import {ShippingErrorHandler} from "./ShippingErrorHandler";
import {ShippingApiService} from "./ShippingApiService";

declare var autoShippedWhenOpeningSO: boolean;
declare var autoShipMultiSOItems: boolean;
declare var isViewOnly: boolean;

export class ShippingWorkflow {
    constructor(private actions: ShippingActions) {}

    async handleWorkflow(
        data: any,
        autoPopup: boolean,
        onSoMulti: (data: any, onShopify: () => void) => void,
        onRegular: (data: any, onShopify: () => void) => void
    ): Promise<void> {
        // In view only mode, disable auto ship functionality
        const autoShipEnabled = !isViewOnly && autoShippedWhenOpeningSO;

        // Check if wave order mode is SO multi
        const isSoMulti = data.wave_order && data.wave_order.is_so_multi === true;

        // Handle SO multi mode with auto-popup
        if (isSoMulti && autoPopup) {
            await this.handleSoMultiAutoPopup(data);
            return;
        }

        if (isSoMulti) {
            // For SO multi mode without auto-popup, show the SO multi modal
            onSoMulti(data, () => this.executeShipAndOpen(data.asset, data.shopify_url));
            return;
        }

        // For non-SO multi modes, check if asset is shipped
        if (data.asset.shipped) {
            ShippingModals.showAlreadyShipped(data.asset);
            return;
        }

        if (autoPopup && autoShipEnabled) {
            await this.handleAutoPopupWithAutoShip(data.asset, data.shopify_url);
            return;
        }

        if (autoPopup) {
            this.handleAutoPopupWithoutAutoShip(data.shopify_url);
            return;
        }

        // Regular modal for non-shipped, non-SO multi assets
        onRegular(data, () => this.executeShipAndOpen(data.asset, data.shopify_url));
    }

    private async handleSoMultiAutoPopup(data: any): Promise<void> {
        // In view only mode, disable auto ship
        const autoShipEnabled = !isViewOnly && autoShippedWhenOpeningSO;
        const autoShipAllEnabled = !isViewOnly && (typeof autoShipMultiSOItems !== 'undefined' ? autoShipMultiSOItems : false);
        const enteredAsset = data.asset;
        const waveOrderAssets = data.wave_order_assets || [];

        // Case 1: Auto ship AND ship all are enabled → ship all assets and open all Shopify URLs
        if (autoShipEnabled && autoShipAllEnabled) {
            // If wave_order_assets is not available, fall back to shipping only entered asset
            if (waveOrderAssets.length === 0) {
                await this.shipEnteredAssetAndOpen(enteredAsset, data.shopify_url);
                return;
            }
            await this.shipAllAndOpenAll(data, waveOrderAssets);
            return;
        }

        // Case 2: Auto ship is enabled BUT ship all is NOT enabled → ship only entered asset and open it
        if (autoShipEnabled && !autoShipAllEnabled) {
            await this.shipEnteredAssetAndOpen(enteredAsset, data.shopify_url);
            return;
        }

        // Case 3: Auto ship AND ship all are both disabled → only open entered asset (don't ship)
        if (!autoShipEnabled && !autoShipAllEnabled) {
            this.actions.openShopify(data.shopify_url);
            return;
        }
    }

    private async shipAllAndOpenAll(data: any, waveOrderAssets: any[]): Promise<void> {
        // Ship all non-shipped assets
        const assetsToShip = waveOrderAssets.filter((asset: any) => !asset.shipped);
        const shippedEpcs = new Set<string>();

        for (const asset of assetsToShip) {
            try {
                const assetId = asset.id ? parseInt(asset.id, 10) : undefined;
                if (assetId) {
                    await ShippingApiService.markShipped(assetId);
                    if (asset.epc) {
                        shippedEpcs.add(asset.epc);
                    }
                }
            } catch (error) {
                ShippingErrorHandler.logError("marking asset as shipped", error);
            }
        }

        // Reload all assets with the same EPCs to ensure all are updated
        for (const epc of Array.from(shippedEpcs)) {
            await this.actions.reloadAllAssetsWithSameEpc(epc);
        }

        // Open all unique Shopify URLs (for both shipped and non-shipped assets)
        const uniqueShopifyUrls = Array.from(
            new Set(
                waveOrderAssets
                    .map((asset: any) => asset.shopify_url || data.shopify_url)
                    .filter((url: string) => url && url.trim() !== "")
            )
        );

        uniqueShopifyUrls.forEach((url: string) => {
            this.actions.openShopify(url);
        });
    }

    private async shipEnteredAssetAndOpen(enteredAsset: any, shopifyUrl?: string): Promise<void> {
        // If asset is already shipped, only open in EOS (don't ship again)
        if (enteredAsset.shipped) {
            this.actions.openShopify(shopifyUrl);
            return;
        }

        if (!enteredAsset.id) {
            ShippingErrorHandler.showWarning("Cannot mark asset as shipped - asset ID is required");
            return;
        }

        const assetId = parseInt(enteredAsset.id, 10);
        await this.actions.markAsShippedSilently(assetId);
        this.actions.openShopify(shopifyUrl);
        await this.actions.reloadAssetInTable(assetId, enteredAsset.epc);
    }

    private async handleAutoPopupWithAutoShip(asset: any, shopifyUrl?: string): Promise<void> {
        if (asset.shipped) {
            this.actions.openShopify(shopifyUrl);
            return;
        }

        if (!asset.id) {
            ShippingErrorHandler.showWarning("Cannot mark asset as shipped - asset ID is required");
            return;
        }

        const assetId = parseInt(asset.id, 10);
        await this.actions.markAsShippedSilently(assetId);
        this.actions.openShopify(shopifyUrl);
        await this.actions.reloadAssetInTable(assetId, asset.epc);
    }

    private handleAutoPopupWithoutAutoShip(shopifyUrl?: string): void {
        this.actions.openShopify(shopifyUrl);
    }

    async executeShipAndOpen(asset: any, shopifyUrl?: string): Promise<void> {
        // In view only mode, don't ship, just open Shopify
        if (!isViewOnly && autoShippedWhenOpeningSO && !asset.shipped && asset.id) {
            const assetId = parseInt(asset.id, 10);
            await this.actions.markAsShippedSilently(assetId);
            await this.actions.reloadAssetInTable(assetId, asset.epc);
        }
        this.actions.openShopify(shopifyUrl);
    }
}

